import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { PartLineView } from "../../../../shared/contracts.js";
import { LIMITS } from "../../../../shared/contracts.js";
import type { CaseStatus } from "../../../../shared/domain.js";
import {
  caseReceiptEndpoints,
  mutationErrorMessage,
  useReverseVerification,
  type CaseMutationResultDto,
  type ReceiptEndpoints,
} from "../../../api/caseMutations.js";
import { Button } from "../../../ui/Button.js";
import { Dialog, DialogActions } from "../../../ui/Dialog.js";
import { FormField } from "../../../ui/FormField.js";

/**
 * Reversing an incorrect verification (spec §2 "reverse an incorrect
 * verification", §7 "Corrections after arrival", §17 "Corrections require a
 * short reason and create a new activity event").
 *
 * Parts picks a line that has something verified, enters the audited
 * **absolute** corrected total (not a delta), gives a 5–500 character reason,
 * and confirms after reading what the correction does: it is kept in the
 * activity history, and when the order is Arrived it goes back to the work
 * queue and recipients get an Arrival corrected email. The request carries
 * the case version the dialog opened with.
 *
 * Version, status and lines are frozen at open, as in `VerifyArrivalDialog`:
 * a parent refetch while the modal is up must not rewrite the confirmation
 * Parts is reading ("from 4 verified to 1") or let the commit ride on a newer
 * version than the one that confirmation was computed from.
 *
 * The same dialog reverses on a wholesale order (wholesale spec §4.5):
 * handed that order's `endpoints` it posts to the wholesale route, and
 * `side="wholesale"` swaps the Arrived consequence — the order leaves the
 * Arrived list and no correction email is sent.
 */

export interface ReverseVerificationDialogProps<R = CaseMutationResultDto> {
  open: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber: string;
  side?: "service" | "wholesale";
  /** Where the reversal is posted; defaults to the case routes for `caseId`. */
  endpoints?: ReceiptEndpoints<R>;
  /** The case version when the dialog opens; frozen for the life of the dialog. */
  version: number;
  /** Frozen at open, like `version`. */
  status: CaseStatus;
  /** Frozen at open, like `version`. */
  partLines: readonly PartLineView[];
  onReversed: (result: R) => void;
}

/** Lines a reversal can apply to: anything with a verified quantity. */
export function reversibleLines(
  partLines: readonly PartLineView[],
): PartLineView[] {
  return partLines.filter((line) => line.quantityVerified > 0);
}

export function ReverseVerificationDialog<R = CaseMutationResultDto>(
  props: ReverseVerificationDialogProps<R>,
) {
  if (!props.open) return null;
  return <ReverseVerificationFlow {...props} />;
}

type Step = "form" | "confirm";

const REASON_ERROR = `The reason must be ${LIMITS.reason.min} to ${LIMITS.reason.max} characters.`;

function ReverseVerificationFlow<R>({
  onClose,
  caseId,
  caseNumber,
  side = "service",
  endpoints,
  onReversed,
  ...live
}: ReverseVerificationDialogProps<R>) {
  // The case as it stood when the dialog opened; `ReverseVerificationDialog`
  // unmounts this flow on close, so every open snapshots afresh.
  const [{ version, status, partLines }] = useState(() => ({
    version: live.version,
    status: live.status,
    partLines: live.partLines,
  }));
  const candidates = useMemo(() => reversibleLines(partLines), [partLines]);
  const [step, setStep] = useState<Step>("form");
  const [lineId, setLineId] = useState<string>(candidates[0]?.id ?? "");
  const [total, setTotal] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [errors, setErrors] = useState<{
    line?: string;
    total?: string;
    reason?: string;
  }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Without endpoints the dialog works the case routes, whose result is the default `R`.
  const [api] = useState(() => endpoints ?? (caseReceiptEndpoints(caseId) as unknown as ReceiptEndpoints<R>));
  const reverse = useReverseVerification(api);

  const lineFieldId = useId();
  const totalFieldId = useId();
  const reasonFieldId = useId();
  const continueRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const line = candidates.find((candidate) => candidate.id === lineId) ?? null;
  const parsedTotal = total.trim() === "" ? Number.NaN : Number(total);
  const trimmedReason = reason.trim();

  // Step changes unmount the focused control; land focus deliberately. The
  // confirmation step starts on Cancel so Enter can never confirm by accident.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (step === "confirm") cancelRef.current?.focus();
    else continueRef.current?.focus();
  }, [step]);

  const validate = (): boolean => {
    const next: typeof errors = {};
    if (!line) {
      next.line = "Choose the part line to correct.";
    }
    if (!Number.isInteger(parsedTotal) || parsedTotal < 0) {
      next.total =
        "Enter the corrected verified total as a whole number, zero or more.";
    } else if (line && parsedTotal >= line.quantityVerified) {
      next.total = `The corrected total must be less than the ${line.quantityVerified} currently verified. To record more parts, use Verify arrival instead.`;
    }
    if (
      trimmedReason.length < LIMITS.reason.min ||
      trimmedReason.length > LIMITS.reason.max
    ) {
      next.reason = REASON_ERROR;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const continueToConfirm = () => {
    setSubmitError(null);
    if (!validate()) return;
    setStep("confirm");
  };

  const commit = () => {
    if (!line) return;
    setSubmitError(null);
    reverse.mutate(
      {
        partLineId: line.id,
        newVerifiedQuantity: parsedTotal,
        reason: trimmedReason,
        expectedVersion: version,
      },
      {
        onSuccess: (result) => {
          onReversed(result);
          onClose();
        },
        onError: (error) => setSubmitError(mutationErrorMessage(error)),
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Reverse verification · ${caseNumber}`}
      description={
        step === "form"
          ? "Correct a verified quantity that was recorded by mistake. Enter the total that is actually verified, not the amount to remove."
          : "Read what this correction does, then confirm it."
      }
      pending={reverse.isPending}
      tone="destructive"
      size="medium"
    >
      {step === "form" ? (
        <>
          <FormField
            id={lineFieldId}
            label="Part line"
            hint="Only lines with a verified quantity are listed."
            error={errors.line ?? null}
          >
            {(control) => (
              <select
                {...control}
                className="text-input select-input"
                data-size="desktop"
                value={lineId}
                disabled={reverse.isPending}
                onChange={(event) => {
                  setLineId(event.target.value);
                  setErrors((current) => ({
                    ...current,
                    line: undefined,
                    total: undefined,
                  }));
                }}
              >
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.partNumber} — {candidate.description} —{" "}
                    {candidate.quantityVerified} of {candidate.quantityOrdered}{" "}
                    verified
                  </option>
                ))}
              </select>
            )}
          </FormField>

          <FormField
            id={totalFieldId}
            label="Corrected verified total"
            hint={
              line
                ? `Currently ${line.quantityVerified} of ${line.quantityOrdered} verified. Enter the number that is truly verified.`
                : undefined
            }
            error={errors.total ?? null}
          >
            {(control) => (
              <input
                {...control}
                type="number"
                inputMode="numeric"
                className="text-input quantity-stepper__input"
                data-size="desktop"
                min={0}
                max={line ? Math.max(0, line.quantityVerified - 1) : undefined}
                step={1}
                value={total}
                disabled={reverse.isPending}
                onChange={(event) => {
                  setTotal(event.target.value);
                  setErrors((current) => ({ ...current, total: undefined }));
                }}
              />
            )}
          </FormField>

          <FormField
            id={reasonFieldId}
            label="Reason"
            hint={`${LIMITS.reason.min}–${LIMITS.reason.max} characters. Kept in the activity history with your name.`}
            error={errors.reason ?? null}
          >
            {(control) => (
              <textarea
                {...control}
                className="text-input textarea-input"
                data-size="desktop"
                rows={3}
                maxLength={LIMITS.reason.max + 100}
                value={reason}
                disabled={reverse.isPending}
                onChange={(event) => {
                  setReason(event.target.value);
                  setErrors((current) => ({ ...current, reason: undefined }));
                }}
              />
            )}
          </FormField>

          {submitError ? (
            <p role="alert" className="form-alert">
              {submitError}
            </p>
          ) : null}

          <DialogActions>
            <Button
              variant="secondary"
              disabled={reverse.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              ref={continueRef}
              variant="primary"
              onClick={continueToConfirm}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      ) : line ? (
        <>
          <section className="correction-summary" aria-label="This correction">
            <p className="correction-summary__change">
              You're changing {line.partNumber} ({line.description}) from{" "}
              {line.quantityVerified} verified to {parsedTotal} verified, out of{" "}
              {line.quantityOrdered} ordered.
            </p>
            <p className="correction-summary__reason">
              <span className="correction-summary__label">Reason</span>{" "}
              {trimmedReason}
            </p>
          </section>

          <section className="receipt-outcome" aria-label="What happens next">
            <p className="receipt-outcome__copy">
              This correction is kept in the activity history with your reason
              and your name. The original verification stays in the history too
              — nothing is erased.
            </p>
            {status === "arrived" && side === "wholesale" ? (
              <p className="receipt-outcome__copy">
                Because the order is Arrived, it leaves the Arrived list right
                away and goes back to Open. No email is sent. Verifying the
                parts again later starts a new 36-hour period.
              </p>
            ) : status === "arrived" ? (
              <p className="receipt-outcome__copy">
                Because the order is Arrived, it goes back to the work queue
                right away, its 48-hour timer is cleared, and the recipients of
                the arrival email get an Arrival corrected email. Verifying the
                parts again later starts a new 48-hour period and sends a new
                arrival email.
              </p>
            ) : (
              <p className="receipt-outcome__copy">
                The order's status is recalculated from its lines after the
                correction.
              </p>
            )}
          </section>

          {submitError ? (
            <p role="alert" className="form-alert">
              {submitError}
            </p>
          ) : null}

          <DialogActions>
            <Button
              variant="secondary"
              disabled={reverse.isPending}
              onClick={() => {
                setSubmitError(null);
                setStep("form");
              }}
            >
              Back
            </Button>
            <Button
              ref={cancelRef}
              variant="secondary"
              disabled={reverse.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              busy={reverse.isPending}
              onClick={commit}
            >
              Reverse verification
            </Button>
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}
